import React, { useState, forwardRef, useEffect, useRef } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./DateTimePicker.module.css";
import { format, getYear, getMonth, setHours, setMinutes, startOfDay } from "date-fns";

interface DateTimePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  placeholderText?: string;
}

const generateTimeOptions = (interval: number) => {
  const times = [];
  for (let i = 0; i < 24 * 60; i += interval) {
    const hours = Math.floor(i / 60);
    const minutes = i % 60;
    const h = hours % 12 === 0 ? 12 : hours % 12;
    const m = minutes.toString().padStart(2, "0");
    const p = hours < 12 ? "AM" : "PM";
    times.push(`${h}:${m} ${p}`);
  }
  return times;
};

const CustomTimeDropdown: React.FC<{
  options: string[];
  selected: string;
  onChange: (value: string) => void;
  styles: any;
}> = ({ options, selected, onChange, styles }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Auto-scroll to selected option when dropdown opens
  useEffect(() => {
    if (isOpen && listRef.current) {
      const selectedIndex = options.indexOf(selected);
      if (selectedIndex !== -1) {
        const optionElement = listRef.current.children[selectedIndex] as HTMLElement;
        if (optionElement) {
          // Scroll the selected option into view
          optionElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
  }, [isOpen, selected, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={styles["custom-time-dropdown"]} ref={dropdownRef}>
      <div
        className={`${styles["time-select-display"]} ${isOpen ? styles["open"] : ""}`}
        onClick={handleToggle}
      >
        {selected}
      </div>
      {isOpen && (
        <div className={styles["time-options-list"]} ref={listRef}>
          {options.map(option => (
            <div
              key={option}
              className={`${styles["time-option"]} ${
                option === selected ? styles["selected-option"] : ""
              }`}
              onClick={() => handleSelect(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CustomInput = forwardRef<
  HTMLDivElement,
  {
    onClick?: () => void;
    selected: Date | null;
    time: string;
    value?: string;
  }
>(({ onClick, selected, time }, ref) => (
  <div
    className="purple-field d-flex align-items-center w-100 border-0 px-3 text-white fw-bold fs-12 height-45 cursor-pointer"
    onClick={onClick}
    ref={ref}
  >
    {selected ? `${format(selected, "dd/MM/yyyy")} ${time}` : "Select Date"}
  </div>
));

const DateTimePicker: React.FC<DateTimePickerProps> = ({ selected, onChange, ...rest }) => {
  const timeOptions = generateTimeOptions(15);

  const formatAndRoundTime = (date: Date | null) => {
    if (!date) return "12:00 AM";
    const minutes = date.getMinutes();
    // Round up to next 15-minute interval
    const roundedMinutes = Math.ceil(minutes / 15) * 15;

    let newDate = new Date(date.getTime());
    if (roundedMinutes === 60) {
      newDate.setHours(newDate.getHours() + 1);
      newDate.setMinutes(0);
    } else {
      newDate.setMinutes(roundedMinutes);
    }
    return format(newDate, "h:mm aa");
  };

  const [time, setTime] = useState(formatAndRoundTime(selected));

  useEffect(() => {
    setTime(formatAndRoundTime(selected));
  }, [selected]);

  const applyTime = (date: Date | null, timeStr: string) => {
    if (!date) return null;
    const [timeVal, period] = timeStr.split(" ");
    let [hours, minutes] = timeVal.split(":").map(Number);
    if (period === "PM" && hours < 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0; // Midnight case
    return setMinutes(setHours(date, hours), minutes);
  };

  const handleDateChange = (date: Date | null) => {
    // Handle edge case: if user selects today's date, round the time to next 15-minute interval
    if (date) {
      const now = new Date();
      const today = startOfDay(now);

      if (startOfDay(date).getTime() === today.getTime()) {
        // User selected today's date, round current time to next 15 minutes
        const currentMinutes = now.getMinutes();
        const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

        let roundedTime = new Date(now.getTime());
        if (roundedMinutes === 60) {
          roundedTime.setHours(roundedTime.getHours() + 1);
          roundedTime.setMinutes(0);
        } else {
          roundedTime.setMinutes(roundedMinutes);
        }

        const roundedTimeStr = format(roundedTime, "h:mm aa");
        setTime(roundedTimeStr);

        // Apply the rounded time to the date
        const dateWithRoundedTime = applyTime(date, roundedTimeStr);
        onChange(dateWithRoundedTime);
        return;
      }
    }

    // Original logic for non-today dates
    onChange(applyTime(date, time));
  };

  const handleTimeChange = (newTime: string) => {
    setTime(newTime);
    onChange(applyTime(selected, newTime));
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className={styles["custom-datetime-picker"]}>
      <ReactDatePicker
        selected={selected}
        onChange={handleDateChange}
        shouldCloseOnSelect={false}
        popperClassName={styles["date-picker-wrapper"]}
        customInput={<CustomInput selected={selected} time={time} />}
        showTimeSelect={false}
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className={styles["custom-header-container"]}>
            <button
              type="button"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              className={`${styles["arrow-btn"]} ${styles["prev-arrow"]}`}
            >
              {"<"}
            </button>

            <div className={styles["custom-header_month-year-container"]}>
              <span className={styles["month-display"]}>{months[getMonth(date)]}</span>
              <span className={styles["year-display"]}>{getYear(date)}</span>
            </div>

            <button
              type="button"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              className={`${styles["arrow-btn"]} ${styles["next-arrow"]}`}
            >
              {">"}
            </button>
          </div>
        )}
        {...rest}
      >
        <div className={styles["date-time-section"]} style={{ color: "black" }}>
          <div className={styles["date-time-row"]}>
            <div>
              <div className={`${styles["date-time-label"]} fw-bold`}>Date</div>
              <div className={styles["date-time-value"]}>
                <div className={styles["date-time-date"]}>
                  {selected ? format(selected, "d") : "-"}
                  <div className={`${styles["date-time-day"]} fw-bold`}>
                    {selected ? format(selected, "eeee") : "Select Date"}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles["date-time-clock"]}>
              <CustomTimeDropdown
                options={timeOptions}
                selected={time}
                onChange={handleTimeChange}
                styles={styles}
              />
            </div>
          </div>
        </div>
      </ReactDatePicker>
    </div>
  );
};

export default DateTimePicker;
